/**
 * Récapitulatif d'un lot d'actions portées par une même phrase (MOKA-208 P4,
 * MOKA-266). Logique pure : `VoiceService.#executeBatch` fournit les résultats,
 * ces fonctions rédigent l'en-tête et l'avertissement de limite.
 */

/** Nom des objets créés quand tout le lot porte sur le même endpoint. */
const BATCH_NOUNS: Record<string, { one: string; many: string }> = {
  'rooms.reserveRoom': { one: 'réservation', many: 'réservations' },
  'project.createTask': { one: 'tâche', many: 'tâches' },
  'project.createProject': { one: 'projet', many: 'projets' }
};

/**
 * En-tête du récapitulatif :
 * - tout a réussi : « 2 réservations faites : » ;
 * - échec partiel : « 1 réservation sur 2 : » — le détail suit, échecs compris ;
 * - rien n'a réussi : « Aucune réservation faite : ».
 * Lot mixte (endpoints différents) : « actions ».
 */
export function batchSummaryHeader(endpoints: readonly string[], succeeded: number, total: number): string {
  const same = endpoints.length > 0 && endpoints.every(e => e === endpoints[0]);
  const noun = (same && BATCH_NOUNS[endpoints[0]]) || { one: 'action', many: 'actions' };
  // « faite » / « faites » s'accordent au féminin (réservation, tâche, action) ; « projet » est masculin.
  const masculine = noun.one === 'projet';
  const done = (plural: boolean) => (masculine ? 'fait' : 'faite') + (plural ? 's' : '');

  if (succeeded === 0) {
    return `${masculine ? 'Aucun' : 'Aucune'} ${noun.one} ${done(false)} :`;
  }
  if (succeeded < total) {
    return `${succeeded} ${succeeded > 1 ? noun.many : noun.one} sur ${total} :`;
  }
  return `${succeeded} ${succeeded > 1 ? noun.many : noun.one} ${done(succeeded > 1)} :`;
}

/**
 * Demandes au-delà de la limite du lot : jamais ignorées en silence (retour de
 * Timothée, MOKA-266). `null` quand rien n'a été écarté.
 */
export function droppedBatchNotice(dropped: number, maxPerBatch: number): string | null {
  if (dropped <= 0) return null;
  return dropped > 1
    ? `${dropped} autres demandes n'ont pas été traitées : je fais ${maxPerBatch} actions au plus à la fois. Redemandez-les ensuite.`
    : `1 autre demande n'a pas été traitée : je fais ${maxPerBatch} actions au plus à la fois. Redemandez-la ensuite.`;
}
